import React, { useRef, useCallback, useState } from 'react';
import { PanelConfig, PanelBounds } from '../../../core/types/panel.types';
import { PanelHeader } from './PanelHeader';
import { usePanelStore } from '../../store/panelStore';
import { cn } from '../../utils/cn';

// Constants for drag bounds
const TOP_OFFSET = 32; // MenuBar height
const HEADER_HEIGHT = 32; // Panel header height (h-8 = 2rem = 32px)
const MIN_VISIBLE_WIDTH = 70; // Minimum visible panel width on screen

/**
 * Clamp panel bounds to keep panel accessible.
 * - Top: constrained to protect the menubar/toolbar area
 * - Bottom: constrained so title bar remains visible for dragging
 * - Left/Right: constrained so at least MIN_VISIBLE_WIDTH remains on screen
 */
const clampPanelBounds = (bounds: PanelBounds, minWidth: number, minHeight: number): PanelBounds => {
  const maxY = window.innerHeight - HEADER_HEIGHT; // Keep title bar visible
  const minX = -bounds.width + MIN_VISIBLE_WIDTH; // Left limit: keep some visible on right
  const maxX = window.innerWidth - MIN_VISIBLE_WIDTH; // Right limit: keep some visible on left
  return {
    ...bounds,
    width: Math.max(minWidth, bounds.width),
    height: Math.max(minHeight, bounds.height),
    x: Math.max(minX, Math.min(maxX, bounds.x)), // Left and right constraint
    y: Math.max(TOP_OFFSET, Math.min(maxY, bounds.y)), // Top and bottom constraint
  };
};

interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  panel: PanelConfig & { animating?: 'opening' | 'closing' | 'minimizing' | 'restoring' | null };
  children: React.ReactNode;
  className?: string;
}

export const Panel: React.FC<PanelProps> = ({ panel, children, className, ...rest }) => {
  const { setActivePanel, activePanelId, setPanelBounds } = usePanelStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const isActive = activePanelId === panel.id;
  const isFloating = panel.state === 'floating';
  const isMaximized = panel.state === 'maximized';
  const isMinimized = panel.state === 'minimized';
  const animating = panel.animating;

  // Animation classes based on state
  const getAnimationClasses = () => {
    if (animating === 'opening' || animating === 'restoring') {
      return 'animate-panel-open';
    }
    if (animating === 'closing' || animating === 'minimizing') {
      return 'animate-panel-close';
    }
    return '';
  };

  const handleMouseDown = useCallback(() => {
    setActivePanel(panel.id);
  }, [panel.id, setActivePanel]);

  // Drag handler for floating panels
  const handleStartDrag = useCallback(
    (e: React.MouseEvent) => {
      if (!isFloating || !panel.bounds) return;
      const el = panelRef.current;
      if (!el) return;

      e.preventDefault();
      setIsDragging(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startBoundsX = panel.bounds.x;
      const startBoundsY = panel.bounds.y;
      const boundsSnapshot = { ...panel.bounds };

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        const newBounds = clampPanelBounds(
          {
            ...boundsSnapshot,
            x: startBoundsX + deltaX,
            y: startBoundsY + deltaY,
          },
          panel.minWidth,
          panel.minHeight
        );

        // Direct DOM update — bypasses React/Zustand for zero-lag dragging
        el.style.left = newBounds.x + 'px';
        el.style.top = newBounds.y + 'px';
        // Stash final position for mouseup
        (el as HTMLDivElement & { _dragBounds?: PanelBounds })._dragBounds = newBounds;
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Sync final position to store once
        const final = (el as HTMLDivElement & { _dragBounds?: PanelBounds })._dragBounds;
        if (final) {
          setPanelBounds(panel.id, final);
          delete (el as HTMLDivElement & { _dragBounds?: PanelBounds })._dragBounds;
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';
    },
    [isFloating, panel.bounds, panel.id, panel.minWidth, panel.minHeight, setPanelBounds]
  );

  // Resize handler for floating panels
  const handleStartResize = useCallback(
    (edge: string) => (e: React.MouseEvent) => {
      if (!isFloating || !panel.bounds) return;

      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);

      const startX = e.clientX;
      const startY = e.clientY;
      const startBounds = { ...panel.bounds };

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        let newBounds = { ...startBounds };

        if (edge.includes('e')) {
          newBounds.width = Math.max(panel.minWidth, startBounds.width + deltaX);
        }
        if (edge.includes('w')) {
          const newWidth = Math.max(panel.minWidth, startBounds.width - deltaX);
          newBounds.x = startBounds.x + (startBounds.width - newWidth);
          newBounds.width = newWidth;
        }
        if (edge.includes('s')) {
          newBounds.height = Math.max(panel.minHeight, startBounds.height + deltaY);
        }
        if (edge.includes('n')) {
          const newHeight = Math.max(panel.minHeight, startBounds.height - deltaY);
          newBounds.y = startBounds.y + (startBounds.height - newHeight);
          newBounds.height = newHeight;
        }

        // Clamp the resized bounds to keep panel within viewport
        newBounds = clampPanelBounds(newBounds, panel.minWidth, panel.minHeight);
        setPanelBounds(panel.id, newBounds);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
    },
    [isFloating, panel.bounds, panel.id, panel.minWidth, panel.minHeight, setPanelBounds]
  );

  if (isMinimized) {
    return null; // Minimized panels shown in taskbar
  }

  if (isMaximized) {
    return (
      <div
        ref={panelRef}
        className={cn(
          'fixed inset-0 z-50 flex flex-col bg-zinc-900',
          // Only transition visual properties, not position
          'transition-[opacity,transform,box-shadow] duration-100',
          isActive && 'ring-1 ring-blue-500',
          getAnimationClasses()
        )}
        onMouseDown={handleMouseDown}
        {...rest}
      >
        <PanelHeader panel={panel} />
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">{children}</div>
      </div>
    );
  }

  if (isFloating && panel.bounds) {
    // Use panel's zIndex for stacking order (higher = on top)
    // Panels stay below menu dropdowns (z-1000) and modals (z-100)
    const panelZIndex = panel.zIndex || 50;

    return (
      <div
        ref={panelRef}
        className={cn(
          'fixed flex flex-col bg-zinc-900 shadow-2xl rounded-lg overflow-hidden',
          // Only transition visual properties, not position (left/top) to keep dragging instant
          !isDragging && 'transition-[opacity,transform,box-shadow] duration-100',
          isActive ? 'ring-2 ring-blue-500' : 'border border-zinc-700',
          isDragging && 'opacity-90',
          getAnimationClasses(),
          className
        )}
        style={{
          left: panel.bounds.x,
          top: panel.bounds.y,
          width: panel.bounds.width,
          height: panel.bounds.height,
          zIndex: panelZIndex,
        }}
        onMouseDown={handleMouseDown}
        {...rest}
      >
        {/* Resize handles — z-10 ensures they stay above panel content */}
        {/* North (top) */}
        <div
          className="absolute inset-x-2 top-0 h-1 z-10 cursor-n-resize hover:bg-blue-500/50"
          onMouseDown={handleStartResize('n')}
        />
        {/* South (bottom) */}
        <div
          className="absolute inset-x-2 bottom-0 h-1 z-10 cursor-s-resize hover:bg-blue-500/50"
          onMouseDown={handleStartResize('s')}
        />
        {/* West (left) */}
        <div
          className="absolute inset-y-2 left-0 w-1 z-10 cursor-w-resize hover:bg-blue-500/50"
          onMouseDown={handleStartResize('w')}
        />
        {/* East (right) */}
        <div
          className="absolute inset-y-2 right-0 w-1 z-10 cursor-e-resize hover:bg-blue-500/50"
          onMouseDown={handleStartResize('e')}
        />
        {/* Corners */}
        <div
          className="absolute top-0 left-0 w-3 h-3 z-10 cursor-nw-resize"
          onMouseDown={handleStartResize('nw')}
        />
        <div
          className="absolute top-0 right-0 w-3 h-3 z-10 cursor-ne-resize"
          onMouseDown={handleStartResize('ne')}
        />
        <div
          className="absolute bottom-0 left-0 w-3 h-3 z-10 cursor-sw-resize"
          onMouseDown={handleStartResize('sw')}
        />
        <div
          className="absolute bottom-0 right-0 w-3 h-3 z-10 cursor-se-resize"
          onMouseDown={handleStartResize('se')}
        />

        <PanelHeader panel={panel} onStartDrag={handleStartDrag} />
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">{children}</div>
      </div>
    );
  }

  // Normal docked panel - no drag, parent controls sizing
  return (
    <div
      ref={panelRef}
      className={cn(
        'flex flex-col bg-zinc-900 border border-zinc-800 overflow-hidden rounded-lg',
        // Only transition visual properties, not position
        'transition-[opacity,transform,box-shadow] duration-100',
        isActive && 'ring-1 ring-blue-500 ring-inset',
        getAnimationClasses(),
        className
      )}
      onMouseDown={handleMouseDown}
      {...rest}
    >
      <PanelHeader panel={panel} />
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">{children}</div>
    </div>
  );
};
